import {
  Box,
  Flex,
  Heading,
  IconButton,
  Image,
  Modal,
  ModalContent,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useBreakpointValue,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import { trackEvent } from '@lib/clinician-tracking'
import { copyToClipboard } from '@utilities'
import React, { useEffect, useState } from 'react'
import { useMutation } from 'react-query'
import {
  growsurfControllerGetInviteLink,
  useGrowsurfControllerGetInviteStatus,
  useGrowsurfControllerGetRewardStatus,
  useGrowsurfControllerGetTierStatus,
  useGrowsurfControllerGetUnseenRewards,
  useGrowsurfControllerMarkRewardsAsSeen
} from '~/clinician-api'
import InviteStatusTab from './invite-status-tab'
import RewardNotificationTab from './reward-notification-tab'
import RewardTiersTab from './reward-tiers-tab'
import SendInvitesTab from './send-invites-tab'
import TermsTab from './terms-tab'

export enum ReferralModalView {
  SendInvites = 0,
  InviteStatus = 1,
  RewardTiers = 2,
  Terms = 3,
  RewardNotification = 4
}

interface ReferralModalProps {
  isOpen: boolean
  onClose: () => void
  initialView?: ReferralModalView
}

interface ReferralModalContentProps {
  showCloseButton?: boolean
  onClose?: () => void
  initialView?: ReferralModalView
}

const tabImages = {
  sendInvites:
    'https://res.cloudinary.com/hellojoy/image/upload/v1745356560/coffee-with-friend_ijj4hc.webp',
  inviteStatus:
    'https://res.cloudinary.com/hellojoy/image/upload/v1745356857/man-on-phone_gri6m3.webp',
  rewardTiers:
    'https://res.cloudinary.com/hellojoy/image/upload/v1745356984/gift-on-table_1_ugt8vn.webp',
  terms:
    'https://res.cloudinary.com/hellojoy/image/upload/v1745357124/blueprint-on-desktop-computer_udzv1s.webp'
}

const ReferralModalContent: React.FC<ReferralModalContentProps> = ({
  showCloseButton = true,
  onClose,
  initialView = ReferralModalView.SendInvites
}) => {
  const [tabIndex, setTabIndex] = useState(initialView)
  const [referralLink, setReferralLink] = useState('')
  const [hasCopied, setHasCopied] = useState(false)
  const showImage = useBreakpointValue({ base: false, md: true, lg: true })
  const toast = useToast()

  const { data: inviteStatusData, isLoading: isLoadingInviteStatus } =
    useGrowsurfControllerGetInviteStatus()
  const { data: tierStatusData, isLoading: isLoadingTierStatus } =
    useGrowsurfControllerGetTierStatus()
  const { data: unseenRewardsData, refetch: refetchUnseenRewards } =
    useGrowsurfControllerGetUnseenRewards()
  const { data: rewardStatusData } = useGrowsurfControllerGetRewardStatus()
  const { mutate: markRewardsAsSeen } = useGrowsurfControllerMarkRewardsAsSeen()
  const hasUnseenRewards =
    unseenRewardsData && unseenRewardsData.newFreeMonthsAdded > 0

  const handleCopy = async (text: string) => {
    try {
      await copyToClipboard(text)
      setHasCopied(true)
      // Reset copied state after 2 seconds
      setTimeout(() => setHasCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
      toast({
        title: 'Copy failed',
        status: 'error',
        duration: 2000,
        isClosable: true
      })
    }
  }

  const { mutate: fetchReferralLink, isLoading: isFetchingLink } = useMutation(
    growsurfControllerGetInviteLink,
    {
      onSuccess: data => {
        if (data.growsurfInviteLink) {
          const link = data.growsurfInviteLink
          setReferralLink(link)

          // Give a small delay to ensure state is updated before copying
          setTimeout(() => {
            handleCopy(link)
          }, 100)
        }
      },
      onError: error => {
        console.error('Failed to fetch referral link', error)
        toast({
          title: "Couldn't generate referral link",
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    }
  )

  const handleCopyClick = () => {
    trackEvent('Referral V3 -> Clicked Copy Link Text', {
      shareMethod: 'copy_link'
    })

    if (!referralLink) {
      fetchReferralLink(undefined)
    } else {
      handleCopy(referralLink)
    }
  }

  const getCurrentImage = () => {
    if (tabIndex === ReferralModalView.RewardNotification) {
      return tabImages.rewardTiers
    }

    switch (tabIndex) {
      case ReferralModalView.SendInvites:
        return tabImages.sendInvites
      case ReferralModalView.InviteStatus:
        return tabImages.inviteStatus
      case ReferralModalView.RewardTiers:
        return tabImages.rewardTiers
      case ReferralModalView.Terms:
        return tabImages.terms
      default:
        return tabImages.sendInvites
    }
  }

  const handleRewardIconClick = () => {
    trackEvent('Referral V3 -> Changed Tab', {
      tab: ReferralModalView[ReferralModalView.RewardNotification]
    })

    setTabIndex(ReferralModalView.RewardNotification)

    if (hasUnseenRewards) {
      markRewardsAsSeen(undefined, {
        onSuccess: () => {
          refetchUnseenRewards()
        },
        onError: error => {
          console.error('Failed to mark rewards as seen', error)
        }
      })
    }
  }

  const paidCount = inviteStatusData?.inviteStatusCounts?.paidCount || 0

  useEffect(() => {
    setTabIndex(initialView)
  }, [initialView])

  return (
    <Flex direction={{ base: 'column', md: 'row' }} height={{ md: '600px' }}>
      {showImage && (
        <Box
          width={{ md: '50%' }}
          borderTopLeftRadius="8px"
          borderBottomLeftRadius="8px"
          height="100%"
          overflow="hidden"
          position="relative"
        >
          <Image
            src={getCurrentImage()}
            objectFit="cover"
            height="100%"
            width="100%"
            alt="Referral illustration"
          />
        </Box>
      )}

      <Box width={{ base: '100%', md: '50%' }} height="100%">
        <Box position="relative" alignItems="center" textAlign="center" p={5}>
          <Box position="absolute" top="12px" left={4}>
            <IconButton
              aria-label="Earned Rewards"
              icon={
                <Box
                  display="flex"
                  alignItems="center"
                  mt="2px"
                  justifyContent="center"
                  fontSize="20px"
                >
                  🎁
                </Box>
              }
              height="40px"
              width="40px"
              variant="ghost"
              background="rgba(45, 84, 232, 0.10)"
              borderRadius="50%"
              border={
                tabIndex === ReferralModalView.RewardNotification
                  ? '2px solid rgb(45, 84, 233)'
                  : '1px solid rgba(45, 84, 232, 0.10)'
              }
              onClick={handleRewardIconClick}
              _focus={{ outline: 'none' }}
              _hover={{
                border: '1px solid rgba(45, 84, 232, 0.15)',
                background: 'rgba(45, 84, 232, 0.15)',
                transform: 'rotate(-12deg)'
              }}
              _active={{
                border: '2px solid rgb(45, 84, 233)'
              }}
              position="relative"
            />
            {hasUnseenRewards && (
              <Box
                position="absolute"
                top="0"
                right="0"
                width="6px"
                height="6px"
                borderRadius="50%"
                bg="#2D54E9"
                transform="translate(3px, 3px)"
                boxSizing="content-box"
              />
            )}
          </Box>
          <Heading as="h2" fontSize="24px">
            Referral Program
          </Heading>
          {showCloseButton && onClose && (
            <Box position="absolute" top={4} right={4}>
              <IconButton
                aria-label="Close"
                icon={<span>✕</span>}
                size="sm"
                variant="ghost"
                onClick={onClose}
                _focus={{ outline: 'none' }}
              />
            </Box>
          )}
        </Box>

        <Tabs
          variant="line"
          colorScheme="blue"
          onChange={index => {
            setTabIndex(index)
            trackEvent('Referral V3 -> Changed Tab', {
              tab: ReferralModalView[index]
            })
          }}
          isFitted
          isLazy
          index={
            tabIndex === ReferralModalView.RewardNotification ? -1 : tabIndex
          }
          display="flex"
          flexDirection="column"
        >
          <TabList>
            <Tab _focus={{ outline: 'none' }}>Send Invites</Tab>
            <Tab _focus={{ outline: 'none' }}>Invite Status</Tab>
            <Tab _focus={{ outline: 'none' }}>Reward Tiers</Tab>
            <Tab _focus={{ outline: 'none' }}>Terms</Tab>
          </TabList>

          {tabIndex === ReferralModalView.RewardNotification ? (
            <Box flex="1" overflow="hidden">
              <RewardNotificationTab
                freeMonths={
                  (unseenRewardsData?.newFreeMonthsAdded || 0) > 0
                    ? unseenRewardsData?.newFreeMonthsAdded
                    : rewardStatusData?.numFreeMonthsRemaining || 0
                }
                onTermsClick={() => {
                  setTabIndex(ReferralModalView.Terms)
                }}
                onSendInvitesClick={() => {
                  setTabIndex(ReferralModalView.SendInvites)
                }}
                onMonitorStatusClick={() => {
                  setTabIndex(ReferralModalView.InviteStatus)
                }}
              />
            </Box>
          ) : (
            <TabPanels>
              <TabPanel>
                <SendInvitesTab
                  handleCopyClick={handleCopyClick}
                  referralLink={referralLink}
                  hasCopied={hasCopied}
                  isFetchingLink={isFetchingLink}
                />
              </TabPanel>

              <TabPanel>
                <InviteStatusTab
                  inviteStatusData={inviteStatusData}
                  isLoadingInviteStatus={isLoadingInviteStatus}
                  paidCount={paidCount}
                  onSendInvitesClick={() => {
                    setTabIndex(ReferralModalView.SendInvites)
                  }}
                />
              </TabPanel>

              <TabPanel data-testid="reward-tiers-tab-panel" padding={0}>
                <RewardTiersTab
                  tierStatusData={tierStatusData}
                  isLoadingTierStatus={isLoadingTierStatus}
                />
              </TabPanel>

              <TabPanel>
                <TermsTab />
              </TabPanel>
            </TabPanels>
          )}
        </Tabs>
      </Box>
    </Flex>
  )
}

const ReferralModal: React.FC<ReferralModalProps> = ({
  isOpen,
  onClose,
  initialView
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={useBreakpointValue({ base: 'xl', lg: '6xl' })}
      isCentered
    >
      <ModalOverlay />
      <ModalContent maxWidth="1200px">
        <ReferralModalContent onClose={onClose} initialView={initialView} />
      </ModalContent>
    </Modal>
  )
}

const ReferralModalContext = React.createContext<{
  openReferralModal: (view?: ReferralModalView) => void
}>({
  openReferralModal: () => {}
})

export const useReferralModalGlobal = () =>
  React.useContext(ReferralModalContext)

export const useReferralModal = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [initialView, setInitialView] = useState<ReferralModalView>(
    ReferralModalView.SendInvites
  )

  const openReferralModal = (viewInput?: ReferralModalView) => {
    setInitialView(
      viewInput !== undefined ? viewInput : ReferralModalView.SendInvites
    )
    onOpen()
  }

  const ReferralModalComponent = React.useCallback(() => {
    return (
      <ReferralModalContext.Provider value={{ openReferralModal }}>
        <ReferralModal
          isOpen={isOpen}
          onClose={onClose}
          initialView={initialView}
        />
      </ReferralModalContext.Provider>
    )
  }, [isOpen, onClose, initialView])

  return {
    ReferralModalComponent,
    openReferralModal
  }
}

export default ReferralModal
